/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Velocity MCP Installation Service
 * 
 * Client-side API for installing the Velocity MCP server to coding agents.
 */

import { getProductServerBaseUrl } from './base';

export type VelocityMcpAgent = 'cursor' | 'claude-code' | 'codex' | 'antigravity';

export interface VelocityMcpAgentStatus {
  agent: VelocityMcpAgent;
  displayName: string;
  installed: boolean;
  configPath: string;
  configFormat: 'json' | 'toml';
  configSnippet: string;
  currentConfig?: Record<string, unknown>;
}

export interface VelocityMcpInstallResult {
  success: boolean;
  agent: VelocityMcpAgent;
  displayName: string;
  configPath?: string;
  alreadyInstalled: boolean;
  message: string;
  error?: string;
}

export interface VelocityMcpInstallAllResult {
  results: Array<{
    agent: VelocityMcpAgent;
    displayName: string;
    success: boolean;
    alreadyInstalled: boolean;
    configPath?: string;
    error?: string;
  }>;
  summary: {
    total: number;
    successful: number;
    failed: number;
    alreadyInstalled: number;
  };
}

/**
 * Get installation status for all agents
 */
export async function getVelocityMcpStatus(): Promise<VelocityMcpAgentStatus[]> {
  const response = await fetch(`${getProductServerBaseUrl()}/api/velocity-mcp/status`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to get Velocity MCP status: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.data?.agents || [];
}

/**
 * Get installation status for a specific agent
 */
export async function getVelocityMcpAgentStatus(agent: VelocityMcpAgent): Promise<VelocityMcpAgentStatus> {
  const response = await fetch(`${getProductServerBaseUrl()}/api/velocity-mcp/status/${agent}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to get Velocity MCP status for ${agent}: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.data;
}

/**
 * Install Velocity MCP server to a specific agent
 */
export async function installVelocityMcp(agent: VelocityMcpAgent): Promise<VelocityMcpInstallResult> {
  const response = await fetch(`${getProductServerBaseUrl()}/api/velocity-mcp/install/${agent}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    return {
      success: false,
      agent,
      displayName: data.data?.displayName || agent,
      alreadyInstalled: false,
      message: data.message || 'Installation failed',
      error: data.message,
    };
  }
  
  return {
    success: true,
    agent: data.data.agent,
    displayName: data.data.displayName,
    configPath: data.data.configPath,
    alreadyInstalled: data.data.alreadyInstalled,
    message: data.data.message,
  };
}

/**
 * Install Velocity MCP server to all agents
 */
export async function installVelocityMcpAll(): Promise<VelocityMcpInstallAllResult> {
  const response = await fetch(`${getProductServerBaseUrl()}/api/velocity-mcp/install-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to install Velocity MCP to all agents: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.data;
}
